import PropTypes from 'prop-types';
import {productLink} from '../../../utils/productLink';
import {ProductGridCard} from './ProductGridCard.client';
import {HomePageHeader} from '~/components/modules/HomePageHeader.client';
import {ProductOptionsProvider, ClientAnalytics, Head} from '@shopify/hydrogen';
import {useEffect, useState} from 'react';

export function ProductGridGallery({
  btnCtaText,
  btnVariant,
  cardsPerRow,
  miniBannersEnabled,
  miniBanners,
  sanityProducts,
  shopifyProducts,
  variantOverrides,
  intellisuggestSignature,
  intellisuggestData,
}) {
  const [yotpoReviews, setYotpoReviews] = useState({});
  // add a new value for the display position of the product to maintain the Shopify sort order
  const shopifyProductsWithPosition = shopifyProducts?.nodes.map(
    (node, index) => {
      if (node) node.displayPosition = index;
      return node;
    },
  );

  // takes in an array of shopify products and a sanity product then returns the single matching shopify product
  const matchingShopifyProduct = (sanityProduct) => {
    const slug = sanityProduct?.store?.slug?.current;

    const filteredShopifyProducts = shopifyProductsWithPosition?.filter(
      (node) => (node?.handle ?? 'a') === (slug ?? 'b'),
    );

    return filteredShopifyProducts?.[0] ?? {};
  };

  // sort based on the Shopify display order, but make sure to preserve variant overrides
  // right now if they exist, because sorting can cause us to lose context of the matching
  // indices in that regard...:
  const sortedSanityProducts = sanityProducts.map((product, i) => {
    if (product) {
      product.matchingShopifyProduct = matchingShopifyProduct(product);

      // ...variant overrides is an array of the same length of the products,
      // if provided, and can have null entries intentionally at any given index:
      product.variantOverride = product?.store?.variants?.find(
        (v) => v?._id === variantOverrides?.[i],
      );
    }

    return product;
  });

  const firstEightItems = sortedSanityProducts.slice(0, 8);

  ClientAnalytics.publish('view_item_list', true, {
    firstEightItems,
  });

  async function fetchYotpoReviews(productId) {
    try {
      const response = await fetch(
        `/api/fetchYotpoReviews?productId=${productId}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        },
      );
      if (!response.ok) {
        throw new Error('Failed to fetch reviews');
      }
      const data = await response.json();
      return {data};
    } catch (error) {
      console.error(
        `Error fetching Yotpo reviews for product ${productId}:`,
        error,
      );
      return {error};
    }
  }

  useEffect(() => {
    sortedSanityProducts.forEach(async (product) => {
      const formattedProductId = product._id.replace('shopifyProduct-', '');
      const {data, error} = await fetchYotpoReviews(formattedProductId);
      setYotpoReviews((prevReviews) => {
        const existingReview = prevReviews[formattedProductId];
        if (
          existingReview &&
          JSON.stringify(existingReview) === JSON.stringify(data)
        ) {
          return prevReviews;
        } else {
          return {...prevReviews, [formattedProductId]: data};
        }
      });
    });
  }, [sortedSanityProducts]);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    numberOfItems: sortedSanityProducts.length,
    itemListElement: sortedSanityProducts.map((sanityProduct, index) => {
      const matchingShopifyProduct = sanityProduct.matchingShopifyProduct;
      const productId = sanityProduct._id.replace('shopifyProduct-', '');
      const yotpoReview = yotpoReviews[productId]?.response?.bottomline;

      // Generate the product URL
      let href = sanityProduct?.store?.slug?.current
        ? `/products/${sanityProduct.store.slug.current}`
        : '/products/not-found';
      href = productLink(href, sanityProduct?.store?.vendor);

      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: matchingShopifyProduct.title,
          brand: sanityProduct.store?.vendor,
          offers: {
            '@type': 'Offer',
            price: matchingShopifyProduct.variants?.nodes?.[0]?.priceV2?.amount,
            priceCurrency: 'USD',
            availability: matchingShopifyProduct.variants?.nodes?.[0]
              ?.availableForSale
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
          },
          image: sanityProduct.store?.previewImageUrl,
          url: href,
          aggregateRating: yotpoReview
            ? {
                '@type': 'AggregateRating',
                bestRating: 5,
                worstRating: 1,
                ratingValue: yotpoReview.average_score.toString(),
                reviewCount: yotpoReview.total_review.toString(),
              }
            : undefined,
        },
      };
    }),
  };

  return (
    <>
      <Head>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Head>
      <div className={'grid-gallery per-row-' + cardsPerRow}>
        {sortedSanityProducts.map((product, index) => {
          if (product._type == 'module.homePageHeader') {
            return (
              <>
                <div className="grid-card product-minibanner">
                  <HomePageHeader
                    key={`minibanner_${index}`}
                    sanityModule={product}
                  />
                </div>{' '}
              </>
            );
          } else {
            const {variantOverride} = product;

            let href = product?.store?.slug?.current
              ? `/products/${product.store.slug.current}`
              : '/products/not-found';
            href = productLink(href, product?.store?.vendor);

            // Loop through options (for now, is always just one), grab the appropriate
            // object key and value from the product's store, and URL encode for linking to the variant:
            if (variantOverride?.store && product?.store?.options?.length) {
              let optionsPath = '';

              product.store.options.forEach(({name}, i) => {
                const shopifyKeyForOption = `option${i + 1}`;
                if (variantOverride.store[shopifyKeyForOption]) {
                  // Append the option value to the path, separated by a dash if it's not the first option
                  optionsPath += `${
                    optionsPath ? '-' : ''
                  }${variantOverride.store[shopifyKeyForOption]
                    .toLowerCase()
                    .replace(/[^a-zA-Z0-9-_.]/g, '-')}`;
                }
              });

              // Append the options path to the href
              href += `-${optionsPath}`;
            }
            const miniBannerData = miniBanners
              ? miniBanners[(index + 1) / 2 - 1]
              : null; // adjust this via sanity
            const minibanner =
              miniBannerData && (index + 1) % 2 === 0 ? ( // adjust this via sanity
                <div className="grid-card product-minibanner">
                  <HomePageHeader
                    key={`minibanner_${index}`}
                    sanityModule={miniBannerData}
                  />
                </div>
              ) : null;

            // NOTE: this slice of the app is probably the most complex.
            // The props here are a result of several small refactors,
            // but we could change the shape to be tidier re: sanity
            // and shopify products being in sync and prop-drilled down to children.
            // However, this works
            return (
              <>
                <ProductOptionsProvider
                  key={product._id}
                  data={product.matchingShopifyProduct}
                >
                  <ProductGridCard
                    index={index}
                    btnCtaText={btnCtaText}
                    btnVariant={btnVariant}
                    href={href}
                    imageOverride={variantOverride?.store?.previewImageUrl}
                    variantOverride={variantOverride}
                    key={`${product._id}_${variantOverride?._id ?? ''}`}
                    pricePreview={
                      product.matchingShopifyProduct.variants?.nodes?.[0]
                        ?.priceV2?.amount
                    }
                    product={product}
                    intellisuggestSignature={intellisuggestSignature?.[index]}
                    intellisuggestData={intellisuggestData?.[index]}
                  ></ProductGridCard>
                </ProductOptionsProvider>
                {miniBannersEnabled ? minibanner : null}
              </>
            );
          }
        })}
      </div>
    </>
  );
}

ProductGridGallery.propTypes = {
  btnCtaText: PropTypes.string.isRequired,
  btnVariant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'dark']),
  cardsPerRow: PropTypes.number.isRequired,
  sanityProducts: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  shopifyProducts: PropTypes.object.isRequired,
  variantOverrides: PropTypes.arrayOf(PropTypes.string),
  intellisuggestSignatures: PropTypes.arrayOf(PropTypes.string),
  intellisuggestData: PropTypes.arrayOf(PropTypes.object),
  miniBanners: PropTypes.arrayOf(PropTypes.object),
};
